import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import { School, Warning, Logout } from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { brandColors } from '../../theme';
import { UserRole } from '../../types';
import { logoutUser } from '../../store/slices/authSlice';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate('/login');
  };

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 50%, ${brandColors.accent} 100%)`,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <School
              sx={{
                fontSize: 48,
                color: 'white',
                mr: 1,
              }}
            />
          </Box>
          <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'white' }}>
            Loading...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Debug: Log user role
  console.log('🔍 Admin App - User Role Check:', { user, role: user?.role });

  // Check if user has UMS_ADMIN role
  // A user may hold several portals; roles is authoritative, role is the legacy fallback.
  const portals = user?.roles?.length ? user.roles : user ? [user.role] : [];
  if (user && !portals.includes(UserRole.UMS_ADMIN)) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 50%, ${brandColors.accent} 100%)`,
          p: 3,
        }}
      >
        <Box sx={{ maxWidth: 500, textAlign: 'center' }}>
          <Warning
            sx={{
              fontSize: 64,
              color: 'white',
              mb: 2,
            }}
          />
          <Typography variant="h4" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
            Access Denied
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body1">
              <strong>UMS Admin Access Required</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              This is the LiveOlympiad University Management System (UMS) for administrators.
              Your account role: <strong>{user.role}</strong>
            </Typography>
          </Alert>
          <Typography variant="body1" sx={{ color: 'white', mb: 3 }}>
            Please contact your administrator to get UMS access, or use the appropriate portal for your role:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
            {user.role === UserRole.LMS_ADMIN && (
              <Typography
                component="a"
                href="https://lms.liveolympiad.app"
                sx={{
                  color: 'white',
                  textDecoration: 'underline',
                  '&:hover': { color: brandColors.accent }
                }}
              >
                → Go to LMS Teacher Portal
              </Typography>
            )}
            {user.role === UserRole.STUDENT && (
              <Typography
                component="a"
                href="https://student.liveolympiad.app"
                sx={{
                  color: 'white',
                  textDecoration: 'underline',
                  '&:hover': { color: brandColors.accent }
                }}
              >
                → Go to Student Portal
              </Typography>
            )}
          </Box>
          <Button
            variant="outlined"
            startIcon={<Logout />}
            onClick={handleLogout}
            sx={{
              color: 'white',
              borderColor: 'white',
              '&:hover': {
                borderColor: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Logout & Switch Account
          </Button>
        </Box>
      </Box>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute; 